import { ProductGallery } from 'modules/product-details/components/product-details-header';
import { Variant } from 'modules/product-details/types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import Slider from 'react-slick';

interface UseSynchoniseVariantImageProps {
  productGallery: ProductGallery[];
  selectedGalleryItemIndex: number;
  selectedVariantId: string | null;
  sliderRef: React.RefObject<Slider>;
  svgLayerRef: React.MutableRefObject<number | undefined>;
  variants: Variant[];
  setSelectedGalleryItemIndex: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Custom hook to synchronise variant image with product gallery slider, svg layer and the full screen gallery
 */
const useSynchoniseVariantImage = ({
  productGallery,
  selectedGalleryItemIndex,
  selectedVariantId,
  sliderRef,
  svgLayerRef,
  variants,
  setSelectedGalleryItemIndex,
}: UseSynchoniseVariantImageProps): void => {
  const { query, isReady } = useRouter();
  const [imageIndex, setImageIndex] = useState<number>();

  useEffect(() => {
    if (!isReady) return;

    const updateImageIndex = (index: number): void => {
      sliderRef.current?.slickGoTo(index);
      setSelectedGalleryItemIndex(index);
      setImageIndex(index);
    };

    const newVariantId = query.variant;

    if (!newVariantId) {
      updateImageIndex(0);
    } else if (newVariantId !== selectedVariantId) {
      const variant = variants.find(
        ({ id }) => id.toString() === newVariantId.toString()
      );
      const variantImageIndex = variant
        ? productGallery.findIndex(
            ({ imageData }) => imageData && imageData.id === variant.image.id
          )
        : -1;

      if (variantImageIndex > -1) updateImageIndex(variantImageIndex);
    }
  }, [
    query,
    isReady,
    productGallery,
    selectedVariantId,
    sliderRef,
    svgLayerRef,
    variants,
    setSelectedGalleryItemIndex,
  ]);

  useEffect(
    () => sliderRef?.current?.slickGoTo(selectedGalleryItemIndex),
    [selectedGalleryItemIndex, sliderRef]
  );

  useEffect(() => {
    if (svgLayerRef.current === undefined && imageIndex !== undefined) {
      svgLayerRef.current = imageIndex;
    }
  });
};

export default useSynchoniseVariantImage;
